import type * as SchemaTypes from '../types';

import type { DummyPrivatePageEndpointContextDataFragment } from './DummyPrivatePageEndpointContextData';
import type { StandardApplicationEntrypointContextDataFragment } from './StandardApplicationEntrypointContextData';
import type { ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment, ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment, ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment } from './ApplicationMarketTypeFragment';
import type { DealerMarketDataFragment } from './DealerMarketData';
import type { BankDealerMarketDataFragment } from './BankDealerMarketData';
import type { NzFeesDealerMarketDataFragment } from './NzFeesDealerMarketData';
import type { TranslatedStringDataFragment } from './TranslatedStringData';
import type { AppointmentModuleSpecsFragment } from './AppointmentModuleSpecs';
import type { SimpleVersioningDataFragment } from './SimpleVersioningData';
import type { AuthorData_CorporateCustomer_Fragment, AuthorData_ExternalBank_Fragment, AuthorData_Guarantor_Fragment, AuthorData_LocalCustomer_Fragment, AuthorData_Salesforce_Fragment, AuthorData_SystemBank_Fragment, AuthorData_User_Fragment } from './AuthorData';
import type { AppointmentTimeSlotDataFragment } from './AppointmentTimeSlotData';
import type { NamirialSigningModuleSpecsFragment } from './NamirialSigningModuleSpecs';
import type { NamirialSettingsSpecFragment } from './NamirialSettingsSpec';
import type { AppointmentModuleEmailContentsSpecsFragment, AppointmentModuleEmailContentCustomerSpecsFragment, AppointmentModuleEmailContentSpecsFragment, AppointmentModuleEmailContentSalesPersonSpecsFragment, AppointmentModuleEmailContentFinderReservationSpecsFragment } from './AppointmentModuleEmailContentsSpecs';
import type { DealerTranslatedStringSettingDataFragment } from './DealerTranslatedStringData';
import type { UploadFileWithPreviewFormDataFragment } from './UploadFileWithPreviewFormData';
import type { InsurerEntrypointContextDataFragment } from './InsurerEntrypointContextData';
import type { DealerDisclaimersConfiguratorDataFragment } from './DealerPriceDisclaimerConfiguratorData';
import type { FlexibleDiscountDataFragment } from './FlexibleDiscountData';
import type { ApplicationListEndpointContextDataFragment } from './ApplicationListEndpointContextData';
import type { EventApplicationEntrypointContextDataFragment } from './EventApplicationEntrypointContextData';
import type { ConfiguratorApplicationEntrypointContextDataFragment } from './ConfiguratorApplicationEntrypointContextData';
import type { MyInfoSettingSpecFragment } from './MyInfoSettingSpec';
import type { CustomerListEndpointContextDataFragment } from './CustomerListEndpointContextData';
import type { MobilityApplicationEntrypointContextDataFragment } from './MobilityApplicationEntrypointContextData';
import type { DealerBookingCodeDataFragment } from './DealerBookingCodeData';
import type { MobilityLocationDataFragment } from './MobilityLocationData';
import type { UserPreviewDataFragment } from './UserPreviewData';
import type { DateUnitDataFragment } from './DateUnitData';
import type { WebpageEndpointContextDataFragment } from './WebpageEndpointContextData';
import type { WebPageEndpointSpecsFragment } from './WebPageEndpointSpecs';
import type { WebPagePathDataFragment } from './WebPagePathData';
import type { StandardApplicationPublicAccessEntrypointContextDataFragment } from './StandardApplicationPublicAccessEntrypointContextData';
import type { DealerApplicationFragmentFragment } from './DealerApplicationFragment';
import type { DealerContactFragmentFragment } from './DealerContactFragment';
import type { DealerSocialMediaFragmentFragment } from './DealerSocialMediaFragment';
import type { DealerDisclaimersFragmentFragment } from './DealerDisclaimersFragment';
import type { DealerIntegrationDetailsFragmentFragment } from './DealerIntegrationDetailsFragment';
import type { FinderApplicationPublicAccessEntrypointContextDataFragment } from './FinderApplicationPublicAccessEntrypointContextData';
import type { EntrypointFinderApplicationPublicModuleFragment } from './EntrypointFinderApplicationPublicModule';
import type { FinderApplicationModuleEmailContentSpecsFragment } from './FinderApplicationModuleEmailContentSpecs';
import type { TranslatedTextDataFragment } from './TranslationTextData';
import type { FinderApplicationEntrypointContextDataFragment } from './FinderApplicationEntrypointContextData';
import type { EntrypointFinderApplicationPrivateModuleFragment } from './EntrypointFinderApplicationPrivateModule';
import { gql } from '@apollo/client';
import { DummyPrivatePageEndpointContextDataFragmentDoc } from './DummyPrivatePageEndpointContextData';
import { StandardApplicationEntrypointContextDataFragmentDoc } from './StandardApplicationEntrypointContextData';
import { ApplicationMarketTypeFragmentFragmentDoc } from './ApplicationMarketTypeFragment';
import { DealerMarketDataFragmentDoc } from './DealerMarketData';
import { BankDealerMarketDataFragmentDoc } from './BankDealerMarketData';
import { NzFeesDealerMarketDataFragmentDoc } from './NzFeesDealerMarketData';
import { TranslatedStringDataFragmentDoc } from './TranslatedStringData';
import { AppointmentModuleSpecsFragmentDoc } from './AppointmentModuleSpecs';
import { SimpleVersioningDataFragmentDoc } from './SimpleVersioningData';
import { AuthorDataFragmentDoc } from './AuthorData';
import { AppointmentTimeSlotDataFragmentDoc } from './AppointmentTimeSlotData';
import { NamirialSigningModuleSpecsFragmentDoc } from './NamirialSigningModuleSpecs';
import { NamirialSettingsSpecFragmentDoc } from './NamirialSettingsSpec';
import { AppointmentModuleEmailContentsSpecsFragmentDoc, AppointmentModuleEmailContentCustomerSpecsFragmentDoc, AppointmentModuleEmailContentSpecsFragmentDoc, AppointmentModuleEmailContentSalesPersonSpecsFragmentDoc, AppointmentModuleEmailContentFinderReservationSpecsFragmentDoc } from './AppointmentModuleEmailContentsSpecs';
import { DealerTranslatedStringSettingDataFragmentDoc } from './DealerTranslatedStringData';
import { UploadFileWithPreviewFormDataFragmentDoc } from './UploadFileWithPreviewFormData';
import { InsurerEntrypointContextDataFragmentDoc } from './InsurerEntrypointContextData';
import { DealerDisclaimersConfiguratorDataFragmentDoc } from './DealerPriceDisclaimerConfiguratorData';
import { FlexibleDiscountDataFragmentDoc } from './FlexibleDiscountData';
import { ApplicationListEndpointContextDataFragmentDoc } from './ApplicationListEndpointContextData';
import { EventApplicationEntrypointContextDataFragmentDoc } from './EventApplicationEntrypointContextData';
import { ConfiguratorApplicationEntrypointContextDataFragmentDoc } from './ConfiguratorApplicationEntrypointContextData';
import { MyInfoSettingSpecFragmentDoc } from './MyInfoSettingSpec';
import { CustomerListEndpointContextDataFragmentDoc } from './CustomerListEndpointContextData';
import { MobilityApplicationEntrypointContextDataFragmentDoc } from './MobilityApplicationEntrypointContextData';
import { DealerBookingCodeDataFragmentDoc } from './DealerBookingCodeData';
import { MobilityLocationDataFragmentDoc } from './MobilityLocationData';
import { UserPreviewDataFragmentDoc } from './UserPreviewData';
import { DateUnitDataFragmentDoc } from './DateUnitData';
import { WebpageEndpointContextDataFragmentDoc } from './WebpageEndpointContextData';
import { WebPageEndpointSpecsFragmentDoc } from './WebPageEndpointSpecs';
import { WebPagePathDataFragmentDoc } from './WebPagePathData';
import { StandardApplicationPublicAccessEntrypointContextDataFragmentDoc } from './StandardApplicationPublicAccessEntrypointContextData';
import { DealerApplicationFragmentFragmentDoc } from './DealerApplicationFragment';
import { DealerContactFragmentFragmentDoc } from './DealerContactFragment';
import { DealerSocialMediaFragmentFragmentDoc } from './DealerSocialMediaFragment';
import { DealerDisclaimersFragmentFragmentDoc } from './DealerDisclaimersFragment';
import { DealerIntegrationDetailsFragmentFragmentDoc } from './DealerIntegrationDetailsFragment';
import { FinderApplicationPublicAccessEntrypointContextDataFragmentDoc } from './FinderApplicationPublicAccessEntrypointContextData';
import { EntrypointFinderApplicationPublicModuleFragmentDoc } from './EntrypointFinderApplicationPublicModule';
import { FinderApplicationModuleEmailContentSpecsFragmentDoc } from './FinderApplicationModuleEmailContentSpecs';
import { TranslatedTextDataFragmentDoc } from './TranslationTextData';
import { FinderApplicationEntrypointContextDataFragmentDoc } from './FinderApplicationEntrypointContextData';
import { EntrypointFinderApplicationPrivateModuleFragmentDoc } from './EntrypointFinderApplicationPrivateModule';
export type EndpointContextData_ApplicationListEndpoint_Fragment = (
  { __typename: 'ApplicationListEndpoint' }
  & Pick<SchemaTypes.ApplicationListEndpoint, 'id' | 'pathname'>
  & ApplicationListEndpointContextDataFragment
);

export type EndpointContextData_ConfiguratorApplicationEntrypoint_Fragment = (
  { __typename: 'ConfiguratorApplicationEntrypoint' }
  & Pick<SchemaTypes.ConfiguratorApplicationEntrypoint, 'id' | 'pathname'>
  & ConfiguratorApplicationEntrypointContextDataFragment
);

export type EndpointContextData_CustomerListEndpoint_Fragment = (
  { __typename: 'CustomerListEndpoint' }
  & Pick<SchemaTypes.CustomerListEndpoint, 'id' | 'pathname'>
  & CustomerListEndpointContextDataFragment
);

export type EndpointContextData_DummyPrivatePageEndpoint_Fragment = (
  { __typename: 'DummyPrivatePageEndpoint' }
  & Pick<SchemaTypes.DummyPrivatePageEndpoint, 'id' | 'pathname'>
  & DummyPrivatePageEndpointContextDataFragment
);

export type EndpointContextData_DummyWelcomePageEndpoint_Fragment = (
  { __typename: 'DummyWelcomePageEndpoint' }
  & Pick<SchemaTypes.DummyWelcomePageEndpoint, 'id' | 'pathname'>
);

export type EndpointContextData_EventApplicationEntrypoint_Fragment = (
  { __typename: 'EventApplicationEntrypoint' }
  & Pick<SchemaTypes.EventApplicationEntrypoint, 'id' | 'pathname'>
  & EventApplicationEntrypointContextDataFragment
);

export type EndpointContextData_FinderApplicationEntrypoint_Fragment = (
  { __typename: 'FinderApplicationEntrypoint' }
  & Pick<SchemaTypes.FinderApplicationEntrypoint, 'id' | 'pathname'>
  & FinderApplicationEntrypointContextDataFragment
);

export type EndpointContextData_FinderApplicationPublicAccessEntrypoint_Fragment = (
  { __typename: 'FinderApplicationPublicAccessEntrypoint' }
  & Pick<SchemaTypes.FinderApplicationPublicAccessEntrypoint, 'id' | 'pathname'>
  & FinderApplicationPublicAccessEntrypointContextDataFragment
);

export type EndpointContextData_MobilityApplicationEntrypoint_Fragment = (
  { __typename: 'MobilityApplicationEntrypoint' }
  & Pick<SchemaTypes.MobilityApplicationEntrypoint, 'id' | 'pathname'>
  & MobilityApplicationEntrypointContextDataFragment
);

export type EndpointContextData_StandardApplicationEntrypoint_Fragment = (
  { __typename: 'StandardApplicationEntrypoint' }
  & Pick<SchemaTypes.StandardApplicationEntrypoint, 'id' | 'pathname'>
  & StandardApplicationEntrypointContextDataFragment
);

export type EndpointContextData_StandardApplicationPublicAccessEntrypoint_Fragment = (
  { __typename: 'StandardApplicationPublicAccessEntrypoint' }
  & Pick<SchemaTypes.StandardApplicationPublicAccessEntrypoint, 'id' | 'pathname'>
  & StandardApplicationPublicAccessEntrypointContextDataFragment
);

export type EndpointContextData_WebPageEndpoint_Fragment = (
  { __typename: 'WebPageEndpoint' }
  & Pick<SchemaTypes.WebPageEndpoint, 'id' | 'pathname'>
  & WebpageEndpointContextDataFragment
);

export type EndpointContextDataFragment = EndpointContextData_ApplicationListEndpoint_Fragment | EndpointContextData_ConfiguratorApplicationEntrypoint_Fragment | EndpointContextData_CustomerListEndpoint_Fragment | EndpointContextData_DummyPrivatePageEndpoint_Fragment | EndpointContextData_DummyWelcomePageEndpoint_Fragment | EndpointContextData_EventApplicationEntrypoint_Fragment | EndpointContextData_FinderApplicationEntrypoint_Fragment | EndpointContextData_FinderApplicationPublicAccessEntrypoint_Fragment | EndpointContextData_MobilityApplicationEntrypoint_Fragment | EndpointContextData_StandardApplicationEntrypoint_Fragment | EndpointContextData_StandardApplicationPublicAccessEntrypoint_Fragment | EndpointContextData_WebPageEndpoint_Fragment;

export const EndpointContextDataFragmentDoc = /*#__PURE__*/ gql`
    fragment EndpointContextData on Endpoint {
  id
  pathname
  __typename
  ... on DummyWelcomePageEndpoint {
    id
    pathname
  }
  ... on DummyPrivatePageEndpoint {
    ...DummyPrivatePageEndpointContextData
  }
  ... on StandardApplicationEntrypoint {
    ...StandardApplicationEntrypointContextData
  }
  ... on ApplicationListEndpoint {
    ...ApplicationListEndpointContextData
  }
  ... on EventApplicationEntrypoint {
    ...EventApplicationEntrypointContextData
  }
  ... on ConfiguratorApplicationEntrypoint {
    ...ConfiguratorApplicationEntrypointContextData
  }
  ... on CustomerListEndpoint {
    ...CustomerListEndpointContextData
  }
  ... on MobilityApplicationEntrypoint {
    ...MobilityApplicationEntrypointContextData
  }
  ... on WebPageEndpoint {
    ...WebpageEndpointContextData
  }
  ... on StandardApplicationPublicAccessEntrypoint {
    ...StandardApplicationPublicAccessEntrypointContextData
  }
  ... on FinderApplicationPublicAccessEntrypoint {
    ...FinderApplicationPublicAccessEntrypointContextData
  }
  ... on FinderApplicationEntrypoint {
    ...FinderApplicationEntrypointContextData
  }
}
    `;